import { useState } from 'react';
import type { PresenceOptimisation, PresenceOptimisationCriterion } from '../../lib/api';
import clsx from '../clsx';
import styles from './OptimisationReadout.module.css';

/**
 * LANE JOB-TRUST G3 — the OptimisationReadout port. Jason's hard ask: "an
 * explanation of the metrics and algorithm each one uses." Re-skinned from
 * apps/web/src/pages/Presence.tsx's OptimisationReadout/CriterionRow (that
 * file's L259-457/L183-257) to this app's CSS-Modules + --tl-* token system —
 * SAME data contract, SAME three honest states, SAME 3-way hybrid
 * (scored/guaranteed/advisory), zero new dependencies (no lucide-react here;
 * plain inline SVG glyphs, matching JobDetail.tsx's own existing idiom).
 *
 * Render-ONLY readout of the server-COMPUTED platform-optimisation score. NO
 * client-side scoring happens here — `opt.score`, `opt.validation_status` and
 * every criterion come straight from the server (PlatformOptimizerService's
 * scorePlatformOptimisation, scored ONLY against VALIDATED platform_criteria,
 * called via the LOCKED presence.routes.ts — this component never touches
 * that route, it only renders what it already served). The colour band is a
 * pure display mapping of the served number, never a recomputation.
 *
 *   - unvalidated -> NO number; "criteria being researched" (we won't show a
 *     score we can't stand behind).
 *   - partial     -> a real score over {validated_count} of {total_count}.
 *   - validated   -> the full per-criterion met/unmet readout.
 */
export function OptimisationReadout({
  opt,
  collapsible = false,
}: {
  opt: PresenceOptimisation;
  /** When true, the scored detail (criteria rows + handled-for-you + keep-in-
   *  mind + stuffing) folds behind a chevron, COLLAPSED by default — the
   *  score number, band colour and bar stay visible in the header. Off = the
   *  full inline readout. */
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const status = opt.validation_status ?? 'unvalidated';
  const platformName = opt.platform ? platformLabel(opt.platform) : 'this platform';
  const criteria = opt.criteria ?? [];
  const scoredCriteria = criteria.filter((c) => (c.display_role ?? 'scored') === 'scored');
  const guaranteed = criteria.filter((c) => c.display_role === 'guaranteed');
  const advisory = criteria.filter((c) => c.display_role === 'advisory');

  const header = (
    <div className={styles.headerLabel}>
      <GaugeIcon /> Platform optimisation
      {opt.platform ? <span className={styles.headerPlatform}>· {platformName}</span> : null}
    </div>
  );

  // STATE 1 — unvalidated: no number, no bar. The honest "we're still
  // researching this platform's criteria" card (no fake score we can't stand
  // behind).
  if (status === 'unvalidated' || typeof opt.score !== 'number') {
    return (
      <div className={styles.card}>
        {header}
        <p className={styles.unvalidatedBody}>
          Platform criteria for {platformName} are being researched — not yet validated. We won't
          show a score we can't stand behind.
        </p>
        <p className={styles.unvalidatedNote}>
          Once we've validated them against real sources, you'll see a scored readout here.
        </p>
      </div>
    );
  }

  // STATE 2 + 3 — partial / validated: a real, served score with the honest
  // denominator.
  const score = Math.max(0, Math.min(100, Math.round(opt.score)));
  const hasDenominator = typeof opt.validated_count === 'number' && typeof opt.total_count === 'number';
  const stuffingTripped = Boolean(opt.stuffing?.scored && opt.stuffing.detected);
  const hasDetail = scoredCriteria.length > 0 || guaranteed.length > 0 || advisory.length > 0 || stuffingTripped;
  const showDetail = !collapsible || open;
  const showToggle = collapsible && hasDetail;

  const headerRow = (
    <div className={styles.headerRow}>
      <div className={styles.headerLeft}>
        {header}
        {showToggle ? <ChevronIcon open={open} /> : null}
      </div>
      <div className={clsx(styles.scoreNum, bandScoreClass(opt.band, score))}>{score}</div>
    </div>
  );

  return (
    <div className={styles.card}>
      {showToggle ? (
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className={styles.toggleBtn}>
          {headerRow}
        </button>
      ) : (
        headerRow
      )}

      <div className={styles.barTrack}>
        <div className={clsx(styles.barFill, bandBarClass(opt.band, score))} style={{ width: `${score}%` }} />
      </div>

      {status === 'partial' && hasDenominator ? (
        <div className={styles.denomNote}>
          Scored against {opt.validated_count} of {opt.total_count} criteria — the rest are still being validated.
        </div>
      ) : hasDenominator ? (
        <div className={styles.denomNote}>
          Scored against {opt.validated_count} validated {opt.validated_count === 1 ? 'criterion' : 'criteria'}.
        </div>
      ) : null}

      {showToggle && !open ? (
        <button type="button" onClick={() => setOpen(true)} className={styles.seeWhatBtn}>
          <InfoIcon /> See what scores you
        </button>
      ) : null}

      {showDetail ? (
        <>
          {scoredCriteria.length > 0 ? (
            <div className={styles.criteriaGroup}>
              {scoredCriteria.map((c) => (
                <CriterionRow key={c.key} c={c} />
              ))}
            </div>
          ) : null}

          {guaranteed.length > 0 ? (
            <div className={styles.criteriaGroup}>
              <div className={clsx(styles.groupLabel, styles.groupLabelGood)}>
                <ShieldIcon /> Handled for you
              </div>
              <div className={styles.criteriaGroup}>
                {guaranteed.map((c) => (
                  <CriterionRow key={c.key} c={c} />
                ))}
              </div>
            </div>
          ) : null}

          {advisory.length > 0 ? (
            <div className={styles.criteriaGroup}>
              <div className={styles.groupLabel}>
                <BulbIcon /> Keep in mind
              </div>
              <div className={styles.criteriaGroup}>
                {advisory.map((c) => (
                  <CriterionRow key={c.key} c={c} />
                ))}
              </div>
            </div>
          ) : null}

          {stuffingTripped ? (
            <div className={styles.stuffingNote}>
              Keyword stuffing detected — ease off these over-repeated terms: {opt.stuffing?.terms.join(', ')}.
              Saying it once, truthfully, is what ranks.
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** One criterion row: the plain-English line is ALWAYS visible (the
 *  explanation IS the line); why-it-matters + the full citation list +
 *  verified-date + confidence sit behind a native <details> tap. Render-only —
 *  `met` and every field come from the server; nothing is scored here. */
function CriterionRow({ c }: { c: PresenceOptimisationCriterion }) {
  const role = c.display_role ?? 'scored';
  const sources = c.source ?? [];
  const verified = c.last_verified_at ? new Date(c.last_verified_at).toLocaleDateString() : null;
  const hasDetail = Boolean(c.why_it_matters) || sources.length > 0 || Boolean(verified) || typeof c.confidence === 'number';

  const glyph =
    role === 'guaranteed' ? (
      <ShieldIcon className={styles.glyphGood} />
    ) : role === 'advisory' ? (
      <BulbIcon className={styles.glyphAccent} />
    ) : c.met ? (
      <CheckIcon className={styles.glyphGood} />
    ) : (
      <DotIcon className={styles.glyphAmber} />
    );

  return (
    <div className={styles.criterionRow}>
      <div className={styles.criterionGlyph}>{glyph}</div>
      <div className={styles.criterionBody}>
        <div className={styles.plainEnglish}>{c.plain_english}</div>
        {role === 'scored' && !c.met && c.fix_hint ? <div className={styles.fixHint}>{c.fix_hint}</div> : null}
        {hasDetail ? (
          <details className={styles.moreInfo}>
            <summary className={styles.moreInfoSummary}>
              <InfoIcon /> Why this matters · source
            </summary>
            <div className={styles.moreInfoBody}>
              {c.why_it_matters ? <p className={styles.whyItMatters}>{c.why_it_matters}</p> : null}
              {sources.length > 0 ? (
                <div className={styles.sourceList}>
                  {sources.map((s) => (
                    <div key={s.url} className={styles.sourceLine}>
                      Source:{' '}
                      <a href={s.url} target="_blank" rel="noreferrer" className={styles.sourceLink}>
                        {s.title || s.url}
                      </a>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className={styles.metaRow}>
                {verified ? <span>Verified {verified}</span> : null}
                {typeof c.confidence === 'number' ? <span>Confidence {c.confidence}/100</span> : null}
              </div>
            </div>
          </details>
        ) : null}
      </div>
    </div>
  );
}

/** Title-case a served platform id for display ("linkedin" -> "LinkedIn").
 *  Mirrors apps/web/src/pages/Presence.tsx's own platformLabel exactly. */
function platformLabel(id: string): string {
  const bare = id.includes(':') ? id.slice(id.indexOf(':') + 1) : id;
  const spaced = bare.replace(/[-_]+/g, ' ').trim();
  return spaced
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/** Pure DISPLAY mapping of the SERVED band to a CSS class — this is not a
 *  score recomputation; `opt.score`/`opt.band` both come from the server. A
 *  missing band falls back to a colour read off the served number. */
function bandScoreClass(band: PresenceOptimisation['band'], score: number): string {
  switch (band) {
    case 'strong':
      return styles.scoreGood;
    case 'good':
      return styles.scoreAccent;
    case 'needs_work':
    case 'thin':
      return styles.scoreAmber;
    default:
      return score >= 80 ? styles.scoreGood : score >= 50 ? styles.scoreAccent : styles.scoreAmber;
  }
}

function bandBarClass(band: PresenceOptimisation['band'], score: number): string {
  switch (band) {
    case 'strong':
      return styles.barGood;
    case 'good':
      return styles.barAccent;
    case 'needs_work':
    case 'thin':
      return styles.barAmber;
    default:
      return score >= 80 ? styles.barGood : score >= 50 ? styles.barAccent : styles.barAmber;
  }
}

// --- inline glyphs (no lucide-react in this app; mirrors JobDetail.tsx's own
// existing inline-SVG idiom) ---------------------------------------------

function GaugeIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" className={styles.iconInline}>
      <path
        d="M12 20a8 8 0 1 0-8-8M12 12l4-4M4 12h1M12 4v1M19 5l-.7.7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true" className={styles.iconInline}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 11v5M12 8v.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={clsx(styles.chevron, open && styles.chevronOpen)}
    >
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={clsx(styles.iconInline, className)}
    >
      <path
        d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BulbIcon({ className }: { className?: string }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={clsx(styles.iconInline, className)}
    >
      <path
        d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3 11.2c.4.3.6.8.6 1.3v.5h4.8v-.5c0-.5.2-1 .6-1.3A6 6 0 0 0 12 3z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={clsx(styles.iconInline, className)}
    >
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DotIcon({ className }: { className?: string }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={clsx(styles.iconInline, className)}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.4" />
    </svg>
  );
}
